import { Box, Card, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarEmoji, F0Button, IconType } from '@factorialco/f0-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type { BenefitListing, BenefitSetupStatus } from 'modules/benefits/lib/benefitListings'

import { BenefitStatusTag } from './BenefitStatusTag'

interface ActionProps {
  label: string
  icon?: IconType
  disabled?: boolean
  loading?: boolean
  onClick: () => void
}

export interface BenefitsCardBenefitActions {
  primaryAction?: ActionProps
  secondaryAction?: ActionProps
}

interface BenefitsCardBenefitProps {
  benefitListing: BenefitListing
  setupStatus: BenefitSetupStatus | undefined
  showTag?: boolean
  actions?: BenefitsCardBenefitActions
}

const BenefitsCardBenefit = (props: BenefitsCardBenefitProps) => {
  const {
    benefitListing: { id, title, description, availability: benefitStatus, emoji, learnMoreUrl },
    setupStatus,
    showTag = true,
    actions,
  } = props

  const handleClick = () => {
    track(trackingEvents.benefits.openBenefitCardInfo, { benefit_type: id })
  }

  const handlePrimaryAction = () => {
    if (actions?.primaryAction?.onClick) {
      track(trackingEvents.benefits.requestBenefit, { benefit_type: id })
      actions.primaryAction.onClick()
    }
  }

  const showFooter = !!learnMoreUrl?.length || !!actions

  // TODO: Switch to F0Card once it supports EmojiAvatars
  return (
    <Card>
      <Card.Eyebrow>
        <Box width="full" flexDirection="row" justifyContent="spaceBetween">
          <F0AvatarEmoji emoji={emoji} size="lg" />

          <Box flexDirection="row" justifyContent="spaceBetween" paddingBottom="s8">
            {showTag ? (
              <Box alignItems="center">
                {benefitStatus ? (
                  <BenefitStatusTag availability={benefitStatus} setupStatus={setupStatus} />
                ) : (
                  <Skeleton width="s80" height="s24" />
                )}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Card.Eyebrow>
      <Card.Content>
        <Box gap="s16" justifyContent="spaceBetween" height="full">
          <Box gap="s2">
            <Text.Normal color="textPrimary" size="300" weight="semibold">
              {title}
            </Text.Normal>

            <Text.Normal color="grey800" size="300">
              {description}
            </Text.Normal>
          </Box>
        </Box>
      </Card.Content>

      {showFooter && (
        <Card.Footer>
          {/*
          This pseudo-footeris an antipattern, but before we switch to F0Card we need to...
          1. Update F0Card support Emoji avatars
          2. Ensure cards are the same height in a row
           */}
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            paddingTop="s12"
            paddingX="s16"
            style={{
              flex: '1',
              marginLeft: '-24px',
              marginRight: '-24px',
              marginBottom: '-4px',
            }}
            border={{
              top: {
                width: 's1',
                style: 'solid',
                color: 'grey300',
              },
            }}
          >
            {/* Secondary actions */}
            <Box gap="s8" flexDirection="row">
              {!!learnMoreUrl?.length && (
                <F0Button
                  variant="outline"
                  onClick={handleClick}
                  label={i18n.t('common.learn_more')}
                  href={learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              )}
              {actions?.secondaryAction && (
                <F0Button
                  label={actions.secondaryAction.label}
                  variant="outline"
                  icon={actions.secondaryAction.icon}
                  onClick={actions.secondaryAction.onClick}
                />
              )}
            </Box>

            {/* Primary action */}
            {actions?.primaryAction && (
              <F0Button
                label={actions.primaryAction.label}
                onClick={handlePrimaryAction}
                icon={actions.primaryAction.icon}
                disabled={actions.primaryAction.disabled}
                loading={actions.primaryAction.loading}
              />
            )}
          </Box>
        </Card.Footer>
      )}
    </Card>
  )
}

export { BenefitsCardBenefit }
